/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { OperationStatus } from "Constants/OperationStatus";
import { AppState } from "Dataflows/Reducers";
import { IMVPUserProfile } from "Interfaces/MVP/IMVPUserProfile";

/**
 * Returns selected user profile.
 * @param appState
 */
export const getSelectedUserProfile = (state: AppState): IMVPUserProfile | undefined =>
  state.mvpState.selectedUserProfileState.selectedUserProfile;

/**
 * Returns loading status of user profile.
 * @param appState
 * @returns {boolean}
 */
export const getUserProfileIsLoadingStatus = (state: AppState): boolean =>
  state.mvpState.selectedUserProfileState.userProfileOperationStatus.read ===
  OperationStatus.Pending;

/**
 * Returns error message fetch user profile fails.
 * @param appState
 * @returns {string}
 */
export const getFetchUserProfileErrorMessage = (state: AppState): string =>
  state.mvpState.selectedUserProfileState.userProfileOperationStatus.read ===
    OperationStatus.Failed && !!state.mvpState.selectedUserProfileState.fetchUserProfileErrorMessage
    ? state.mvpState.selectedUserProfileState.fetchUserProfileErrorMessage
    : "";

export const getUserProfileUpdateBlockShippingStatus = (state: AppState): string =>
  state.mvpState.selectedUserProfileState.updateBlockShippingOperationStatus;

/**
 * Returns update user profile is private error.
 * @param appState
 * @returns {string}
 */
export const getUserProfileUpdateIsPrivateError = (state: AppState): string =>
  state.mvpState.selectedUserProfileState.updateIsPrivateProfileError;

/**
 * Returns update user profile is private operation status.
 * @param appState
 * @returns {OperationStatus}
 */
export const getUserProfileUpdateIsPrivateOperationStatus = (state: AppState): OperationStatus =>
  state.mvpState.selectedUserProfileState.updateIsPrivateProfileStatus;

/**
 * Returns update user profile primary email operation status.
 * @param appState
 * @returns {OperationStatus}
 */
export const getUserProfileUpdatePrimaryEmailStatus = (state: AppState): OperationStatus =>
  state.mvpState.selectedUserProfileState.updatePrimaryEmailStatus;

/**
 * Returns update user profile primary email error.
 * @param appState
 * @returns {string}
 */
export const getUserProfileUpdatePrimaryEmailErrorMessage = (state: AppState): string =>
  state.mvpState.selectedUserProfileState.updatePrimaryEmailErrorMessage;

/**
 * Returns update user profile annual exception error.
 * @param appState
 * @returns {string}
 */
export const getUserProfileUpdateAnnualExceptionError = (state: AppState): string =>
  state.mvpState.selectedUserProfileState.updateAnnualExceptionProfileError;

/**
 * Returns update user profile annual exception operation status.
 * @param appState
 * @returns {OperationStatus}
 */
export const getUserProfileUpdateAnnualExceptionOperationStatus = (
  state: AppState
): OperationStatus => state.mvpState.selectedUserProfileState.updateAnnualExceptionProfileStatus;
